import React, { useEffect, useState } from "react";
import api, { ApiResponse } from "../../services/api";
import VideoLoader from "../../components/VideoLoader";
import { NotepadText, ChevronDown } from "lucide-react";
import RevenueChart from "./RevenueChart";
import OrderTypeCard from "./OrderTypeCard";
import TrendingMenusCard from "./TrendingMenusCard";
import DisabledModifiersCard from "./DisabledModifiersCard";
import { getTokenInfo } from "../../utils/auth";

interface Order {
  _id: string;
  orderNumber: string;
  totalAmount: number;
  orderBy: string;
  createdAt: string;
  choice: string;
  payChoice: string;
}

interface CurrentMonthChoiceCounts {
  diningIn: number;
  takeaway: number;
  online: number;
}

interface RevenuePoint {
  label: string;
  revenue: number;
}

interface RevenueData {
  today: RevenuePoint[];
  thisWeek: RevenuePoint[];
  thisMonth: RevenuePoint[];
}

interface TrendingItem {
  id: string;
  name: string;
  imageUrl: string;
}

interface TrendingItems {
  daily: TrendingItem | null;
  weekly: TrendingItem | null;
  monthly: TrendingItem | null;
}

interface Hotel {
  _id: string;
  hotelName: string;
}

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalOrders, setTotalOrders] = useState(0);
  const [activeUsers, setactiveUsers] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [averageOrderValue, setAverageOrderValue] = useState(0);
  const [currentMonthChoiceCounts, setCurrentMonthChoiceCounts] =
    useState<CurrentMonthChoiceCounts>({
      diningIn: 0,
      takeaway: 0,
      online: 0,
    });
  const [revenueData, setRevenueData] = useState<RevenueData>({
    today: [],
    thisWeek: [],
    thisMonth: [],
  });
  const [trendingItems, setTrendingItems] = useState<TrendingItems>({
    daily: null,
    weekly: null,
    monthly: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  // Location filter state (master_admin only)
  const tokenInfo = getTokenInfo();
  const isMasterAdmin = tokenInfo?.role === "master_admin";
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("all");
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  // Fetch hotels for master_admin
  useEffect(() => {
    if (isMasterAdmin) {
      api
        .get("/admin/getHotelInformation")
        .then((res) => {
          if (res.data.data) {
            setHotels(res.data.data);
          }
        })
        .catch(() => {
          // Silently fail - dropdown just won't show options
        });
    }
  }, [isMasterAdmin]);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, selectedLocation]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(ordersPerPage),
      });
      if (isMasterAdmin && selectedLocation !== "all") {
        params.append("restaurantId", selectedLocation);
      }
      const res = await api.get<ApiResponse<Order[]>>(
        `/orders/getDashboardData?${params.toString()}`,
      );
      if (res.data.success) {
        setOrders(res.data.data);
        setTotalOrders(res.data.totalOrders || 0);
        setactiveUsers(res.data.activeUsers || 0);
        setTotalRevenue(res.data.totalRevenue || 0);
        setAverageOrderValue(res.data.averageOrderValue || 0);
        setCurrentMonthChoiceCounts({
          diningIn: res.data.currentMonthChoiceCounts?.diningIn || 0,
          takeaway: res.data.currentMonthChoiceCounts?.takeaway || 0,
          online: res.data.currentMonthChoiceCounts?.online || 0,
        });
        setRevenueData({
          today: (res.data.revenueData?.today || []).map((item) => ({
            label: item.label || "",
            revenue: Number(item.revenue || 0),
          })),
          thisWeek: (res.data.revenueData?.thisWeek || []).map((item) => ({
            label: item.label || "",
            revenue: Number(item.revenue || 0),
          })),
          thisMonth: (res.data.revenueData?.thisMonth || []).map((item) => ({
            label: item.label || "",
            revenue: Number(item.revenue || 0),
          })),
        });
        const parseTrendingItem = (item: any): TrendingItem | null => {
          if (!item) return null;
          return {
            id: item.id || "",
            name: item.name || "",
            imageUrl: item.imageUrl || "",
          };
        };
        setTrendingItems({
          daily: parseTrendingItem(res.data.trendingItems?.daily),
          weekly: parseTrendingItem(res.data.trendingItems?.weekly),
          monthly: parseTrendingItem(res.data.trendingItems?.monthly),
        });
      }
    } catch {
      setError("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const sortedOrders = [...orders].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const totalPages = Math.ceil(totalOrders / ordersPerPage);
  const paginatedOrders = sortedOrders;
  const totalChoices =
    currentMonthChoiceCounts.diningIn +
    currentMonthChoiceCounts.takeaway +
    currentMonthChoiceCounts.online;

  const orderTypeItems = [
    {
      label: "Dine-In",
      value: currentMonthChoiceCounts.diningIn,
      percent:
        totalChoices > 0
          ? Math.round((currentMonthChoiceCounts.diningIn / totalChoices) * 100)
          : 0,
    },
    {
      label: "Takeaway",
      value: currentMonthChoiceCounts.takeaway,
      percent:
        totalChoices > 0
          ? Math.round((currentMonthChoiceCounts.takeaway / totalChoices) * 100)
          : 0,
    },
    {
      label: "Online",
      value: currentMonthChoiceCounts.online,
      percent:
        totalChoices > 0
          ? Math.round((currentMonthChoiceCounts.online / totalChoices) * 100)
          : 0,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <VideoLoader />
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="mx-auto w-full max-w-7xl p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="mb-4 flex flex-row gap-3 rounded-lg border p-3 sm:mb-6 items-center justify-between sm:p-4">
          <div className="flex flex-col">
            <h1 className="m-0 text-lg font-bold text-gray-800 sm:text-xl">
              Dashboard
            </h1>
            <span className="text-xs sm:text-sm">Welcome Back!</span>
          </div>
          {isMasterAdmin && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span>
                  {selectedLocation === "all"
                    ? "All Locations"
                    : hotels.find((h) => h._id === selectedLocation)
                        ?.hotelName || "Select Location"}
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${locationDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {locationDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setLocationDropdownOpen(false)}
                  />
                  <div className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="max-h-64 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLocation("all");
                          setLocationDropdownOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                          selectedLocation === "all"
                            ? "bg-blue-50 font-semibold text-blue-600"
                            : "text-gray-700"
                        }`}
                      >
                        All Locations
                      </button>
                      {hotels.map((hotel) => (
                        <button
                          key={hotel._id}
                          type="button"
                          onClick={() => {
                            setSelectedLocation(hotel._id);
                            setLocationDropdownOpen(false);
                            setCurrentPage(1);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                            selectedLocation === hotel._id
                              ? "bg-blue-50 font-semibold text-blue-600"
                              : "text-gray-700"
                          }`}
                        >
                          {hotel.hotelName}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {error && <div className="error text-red-600 mb-4">{error}</div>}

        <div className="stats-grid mb-4 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:gap-4 sm:p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
              <NotepadText size={18} strokeWidth={2.1} />
            </div>
            <div>
              <h3 className="m-0 text-[11px] font-semibold text-blue-600 sm:text-[12px]">
                Total Revenue (This Month)
              </h3>
              <div className="stat-value text-lg font-bold sm:text-xl">
                ${totalRevenue.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:gap-4 sm:p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
              <NotepadText size={18} strokeWidth={2.1} />
            </div>
            <div>
              <h3 className="m-0 text-[11px] font-semibold text-blue-600 sm:text-[12px]">
                Total Orders (This Month)
              </h3>
              <div className="stat-value text-lg font-bold sm:text-xl">
                {totalOrders}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:gap-4 sm:p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
              <NotepadText size={18} strokeWidth={2.1} />
            </div>
            <div>
              <h3 className="m-0 text-[11px] font-semibold text-blue-600 sm:text-[12px]">
                Total Active Users (This Month)
              </h3>
              <div className="stat-value text-lg font-bold sm:text-xl">
                {activeUsers}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 sm:gap-4 sm:p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
              <NotepadText size={18} strokeWidth={2.1} />
            </div>
            <div>
              <h3 className="m-0 text-[11px] font-semibold text-blue-600 sm:text-[12px]">
                Avg Order Value (This Month)
              </h3>
              <div className="stat-value text-lg font-bold sm:text-xl">
                ${averageOrderValue.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 flex w-full flex-col gap-4 xl:flex-row">
          <div className="w-full xl:w-[68%]">
            <RevenueChart
              revenueData={revenueData}
              totalRevenue={totalRevenue}
            />
          </div>
          <div className="w-full xl:w-[32%] flex flex-col gap-4">
            <OrderTypeCard items={orderTypeItems} />
            <TrendingMenusCard trendingItems={trendingItems} />
          </div>
        </div>

        <DisabledModifiersCard
          selectedLocation={selectedLocation}
          isMasterAdmin={isMasterAdmin}
        />

        <div className="orders bg-white rounded-xl border border-[rgba(0,0,0,0.1)] overflow-hidden">
          <div className="flex w-full flex-col gap-3 bg-gray-100 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="m-0 text-base font-bold text-gray-700 sm:text-lg">
                Recent Orders
              </h2>
            </div>
          </div>
          {loading ? (
            <div className="py-10 flex justify-center">
              <VideoLoader size="small" />
            </div>
          ) : paginatedOrders.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No recent orders.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                  <tr className="border-y border-gray-200">
                    <th className="p-2.5 font-semibold text-start sm:p-3">
                      Order ID
                    </th>
                    <th className="p-2.5 font-semibold text-center align-middle sm:p-3">
                      Order Type
                    </th>
                    <th className="p-2.5 font-semibold text-center align-middle sm:p-3">
                      Payment Mode
                    </th>
                    <th className="p-2.5 font-semibold text-center align-middle sm:p-3">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order) => (
                    <tr
                      key={order._id}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      <td
                        className="p-2.5 text-xs text-[rgba(0,0,0,0.7)] text-start sm:p-3 sm:text-sm whitespace-nowrap"
                        data-label="Order ID"
                      >
                        Order #{order.orderNumber}
                      </td>
                      <td
                        className="p-2.5 text-xs text-[rgba(0,0,0,0.7)] text-center align-middle sm:p-3 sm:text-sm"
                        data-label="Order Type"
                      >
                        {order.choice
                          ? (() => {
                              const choice = order.choice.toLowerCase();

                              if (choice === "online") return "Online";

                              if (choice === "dining in") return "Dining In";

                              if (choice === "takeaway") return "Takeaway";

                              return order.choice;
                            })()
                          : order.orderBy === "customer"
                            ? "Kiosk"
                            : order.orderBy === "employee"
                              ? "Sales"
                              : ""}
                      </td>
                      <td
                        className="p-2.5 text-xs text-[rgba(0,0,0,0.7)] text-center align-middle sm:p-3 sm:text-sm whitespace-nowrap"
                        data-label="Payment Mode"
                      >
                        {order?.payChoice || "Cash"}
                      </td>
                      <td
                        className="p-2.5 text-xs text-[rgba(0,0,0,0.7)] text-center align-middle sm:p-3 sm:text-sm whitespace-nowrap"
                        data-label="Date"
                      >
                        {new Date(order.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {sortedOrders.length > 0 && totalPages > 1 && (
          <div className="flex justify-center mt-4">
            <div className="flex flex-wrap justify-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="rounded bg-gray-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`rounded px-2.5 py-1 text-xs sm:px-3 sm:text-sm ${
                    currentPage === i + 1
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                className="rounded bg-gray-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
